/* hs-eslint ignored failing-rules */

/* eslint-disable hubspot-dev/no-unsafe-i18n-at-module-eval */
'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { getLongDisplayName } from 'MeetingsLib/utils/utils';
import ProfileHeader from 'MeetingsLib/components/header/ProfileHeader';
import FreemiumUpgradeButton from 'MeetingsLib/components/freemium/FreemiumUpgradeButton';
import BookingStepIndicator from 'MeetingsLib/components/sections/BookingStepIndicator';
import { track } from 'MeetingsLib/tracking/UsageTracker';
import Branding from 'MeetingsBase/models/Branding';
import UserProfile from 'MeetingsBase/models/UserProfile';
import getFreemiumViralLink from 'MeetingsLib/components/freemium/getFreemiumViralLink';
import { THANKYOU_VIRAL_HUBS_CTA } from 'MeetingsLib/constants/paths';
import { BOOKING_STEPS_END } from 'MeetingsLib/constants/steps';
import { redirectToUrl } from 'MeetingsLib/utils/utils';
import { SECOND, REDIRECT_DELAY_IN_SECONDS } from 'MeetingsLib/constants/time';
import H4 from 'UIComponents/elements/headings/H4';
import { useCallback, useContext, useEffect, useState } from 'react';
import PreviewContext from '../../utils/PreviewContext'; // consider dynamic import

import FormattedCurrency from 'I18n/components/FormattedCurrency';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';

function Advert({
  portalId,
  embedded,
  isSalesStarter,
  branding
}) {
  const goToMeetingsPage = useCallback(() => {
    track('meetingsLibClickPromoLink', {
      page: 'thank you'
    });
    const thankYouViralLink = getFreemiumViralLink(THANKYOU_VIRAL_HUBS_CTA, portalId, embedded, true);
    window.open(thankYouViralLink, '_blank', 'noopener nofollow');
  }, [portalId, embedded]);

  if (isSalesStarter) {
    return null;
  }

  return /*#__PURE__*/_jsx(UIFlex, {
    align: "center",
    justify: "center",
    children: /*#__PURE__*/_jsx(FreemiumUpgradeButton, {
      handleButtonClick: () => goToMeetingsPage(),
      branding: branding,
      portalId: portalId,
      embedded: embedded
    })
  });
}

function RedirectCountdown({
  willRedirect,
  timeTillRedirect
}) {
  if (willRedirect) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      className: "p-top-4",
      layout: "centered",
      showLabel: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "summary.redirect",
        options: {
          seconds: timeTillRedirect
        }
      })
    });
  }

  return null;
}

export default function BookingSummary({
  bookedDateTime,
  branding,
  embedded,
  isSalesStarter,
  offline,
  profile,
  userTimezone,
  onSuccess,
  redirectUrl,
  allowRedirect,
  portalId,
  compact,
  isPaidMeeting,
  isPreviewed,
  paidMeetingInfo,
  paidMeetingPrice
}) {
  const isPreview = useContext(PreviewContext);
  const [timeTillRedirect, setTimeTillRedirect] = useState(REDIRECT_DELAY_IN_SECONDS);
  const organizerEmail = profile.email;
  const organizerTemporarilyUnavailable = organizerEmail === '';
  const willRedirect = redirectUrl && isSalesStarter && allowRedirect;
  const updateTimeTillRedirect = useCallback(() => {
    setTimeTillRedirect(_timeTillRedirect => _timeTillRedirect > 1 ? _timeTillRedirect - 1 : 1);
  }, [setTimeTillRedirect]);
  const redirectToCustomPage = useCallback(() => {
    redirectToUrl(redirectUrl, embedded);
  }, [redirectUrl, embedded]);
  useEffect(() => {
    let redirectInterval;
    let redirectTimeout;
    onSuccess(); // check isPreview here because we want to show the redirect UI in the
    // preview, but not actually start any timers

    if (willRedirect && !isPreview) {
      redirectInterval = window.setInterval(updateTimeTillRedirect, SECOND);
      redirectTimeout = window.setTimeout(redirectToCustomPage, REDIRECT_DELAY_IN_SECONDS * SECOND);
    }

    return () => {
      if (redirectTimeout) {
        window.clearTimeout(redirectTimeout);
      }

      if (redirectInterval) {
        window.clearInterval(redirectInterval);
      }
    };
  }, [onSuccess, willRedirect, isPreview, updateTimeTillRedirect, redirectToCustomPage]);
  const classes = 'book-summary m-x-auto' + (!embedded ? " p-top-12" : "");
  const shouldRenderDateTime = !willRedirect;
  let suffix = offline ? 'offline' : 'online';
  suffix = offline && organizerTemporarilyUnavailable ? 'offline_temp_unavailable' : suffix;
  const redirect = willRedirect ? 'redirect.' : '';
  const preMessageKey = `summary.tagline.${redirect}${suffix}`;

  const renderConfirmTagline = () => {
    if (isPaidMeeting) {
      const paidPreMessageKey = `${preMessageKey}_paid`;

      const currencyAndAmount = /*#__PURE__*/_jsx(FormattedCurrency, {
        value: paidMeetingPrice,
        currencyCode: paidMeetingInfo.currency
      });

      return /*#__PURE__*/_jsx(FormattedReactMessage, {
        "data-selenium": "paid-meeting-success-summary",
        "data-selenium-amount": paidMeetingPrice,
        message: paidPreMessageKey,
        options: {
          amount: currencyAndAmount,
          fullName: getLongDisplayName(profile)
        }
      });
    } else {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: preMessageKey,
        options: {
          fullName: getLongDisplayName(profile)
        }
      });
    }
  };

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(BookingStepIndicator, {
      compact: compact,
      currStep: BOOKING_STEPS_END.SUMMARY,
      embedded: embedded,
      isPreviewed: isPreviewed,
      withPayment: isPaidMeeting
    }), /*#__PURE__*/_jsx("div", {
      className: classes,
      "data-timestamp": bookedDateTime,
      children: /*#__PURE__*/_jsx(UICardWrapper, {
        className: "width-100 p-all-0 height-100",
        "data-selenium-test": "booking-summary-step",
        children: /*#__PURE__*/_jsx(UICardSection, {
          className: "p-all-0 height-100",
          children: /*#__PURE__*/_jsxs("div", {
            className: "m-x-4 p-y-11 p-x-3 text-center",
            children: [/*#__PURE__*/_jsx(ProfileHeader, {
              booked: true,
              offline: offline,
              profile: profile
            }), /*#__PURE__*/_jsx("p", {
              className: "text-center m-top-3",
              children: renderConfirmTagline()
            }), shouldRenderDateTime && /*#__PURE__*/_jsxs(H4, {
              className: "m-bottom-1 m-top-5",
              "data-selenium-test": `booked-dateTime-${bookedDateTime}`,
              children: [I18n.moment(bookedDateTime).tz(userTimezone).format('LL'), /*#__PURE__*/_jsx("br", {}), I18n.moment(bookedDateTime).tz(userTimezone).format('LT')]
            }), /*#__PURE__*/_jsx(Advert, {
              portalId: portalId,
              embedded: embedded,
              branding: branding,
              isSalesStarter: isSalesStarter
            }), /*#__PURE__*/_jsx(RedirectCountdown, {
              willRedirect: willRedirect,
              timeTillRedirect: timeTillRedirect
            })]
          })
        })
      })
    })]
  });
}
BookingSummary.propTypes = {
  bookedDateTime: PropTypes.number.isRequired,
  branding: PropTypes.instanceOf(Branding).isRequired,
  embedded: PropTypes.bool.isRequired,
  isSalesStarter: PropTypes.bool,
  offline: PropTypes.bool,
  profile: PropTypes.instanceOf(UserProfile),
  userTimezone: PropTypes.oneOf(I18n.moment.tz.names()),
  onSuccess: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string,
  allowRedirect: PropTypes.bool,
  portalId: PropTypes.number,
  compact: PropTypes.bool.isRequired,
  isPreviewed: PropTypes.bool,
  isPaidMeeting: PropTypes.bool,
  paidMeetingInfo: PropTypes.object,
  paidMeetingPrice: PropTypes.number
};